/**
 * @fileoverview Standalone POD claim overlay.
 *
 * Purpose: Open a Proof of Debt claim's detail/adjudication slide-over from
 * anywhere in the app (e.g. a report citation in the report builder) without
 * navigating to the Recoveries phase. Bundles the claim panel together with the
 * sibling document and contact viewers it hands off to — these mount as
 * siblings rather than nested inside the claim Dialog, since a Radix Sheet
 * nested inside another renders behind its parent's overlay.
 *
 * Used by: RecoveriesView surfaces claims inline; this wrapper lets other
 * surfaces reuse the same experience by passing just a jobId + claimId.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { DocumentDetail } from "../gather/documents/DocumentDetail";
import { ContactDetailPanel } from "../contacts/ContactDetailPanel";
import { PodClaimPanel } from "./PodClaimPanel";

/**
 * Opens `claimId`'s POD claim slide-over in place. Renders nothing when
 * `claimId` is null.
 *
 * @component
 */
export function PodClaimOverlay({
  jobId,
  claimId,
  onClose,
}: {
  jobId: Id<"jobs">;
  claimId: Id<"podClaims"> | null;
  onClose: () => void;
}) {
  // Merge retargeting and the document/contact viewers are owned here so they
  // mount as siblings of the claim Dialog (see file header).
  const [selectedClaimId, setSelectedClaimId] =
    useState<Id<"podClaims"> | null>(claimId);
  useEffect(() => {
    setSelectedClaimId(claimId);
  }, [claimId]);

  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [viewingContactId, setViewingContactId] =
    useState<Id<"jobContacts"> | null>(null);

  const { data: rawClaims } = useQuery({
    ...convexQuery(api.jobs.recoveries.publicQueries.listPodClaimsForJob, {
      jobId,
    }),
    // Only needed once a claim is actually open (merge-target candidates).
    enabled: claimId !== null,
  });
  const { data: jobContacts } = useQuery({
    ...convexQuery(api.contacts.queries.listJobContacts, { jobId }),
    enabled: viewingContactId !== null,
  });
  const { data: relatedFlags } = useQuery({
    ...convexQuery(
      api.jobs.summarization.agents.relatedParties.publicQueries
        .listRelatedPartyFlagsForJob,
      { jobId },
    ),
    enabled: claimId !== null,
  });

  // Join relatedness from the related-parties register, mirroring RecoveriesView
  // so the merge picker shows the same signals.
  const claims = useMemo(() => {
    const rows = rawClaims ?? [];
    if (!relatedFlags?.claims.length) return rows;
    const byClaim = new Map(
      relatedFlags.claims.map(({ claimId: id, ...match }) => [id, match]),
    );
    return rows.map((claim) => {
      const relatedParty = byClaim.get(claim._id);
      return relatedParty ? { ...claim, relatedParty } : claim;
    });
  }, [rawClaims, relatedFlags]);

  return (
    <>
      <PodClaimPanel
        claimId={selectedClaimId}
        claims={claims}
        onClose={() => {
          setSelectedClaimId(null);
          onClose();
        }}
        onViewDocument={setViewingDocumentId}
        onViewContact={setViewingContactId}
        onMerged={(targetId) => setSelectedClaimId(targetId)}
      />

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />

      <ContactDetailPanel
        jobId={jobId}
        contactId={viewingContactId}
        open={viewingContactId !== null}
        onClose={() => setViewingContactId(null)}
        jobContacts={jobContacts ?? []}
      />
    </>
  );
}
